import { isCalendarDate } from "../../domain/round/round-date.js";

/** Relative time in the wording of the original app ("21 days ago"). */
export function timeAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return "Not yet";
  if (isCalendarDate(iso)) return longDate(iso);
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Not yet";
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  if (seconds < 45) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

/** "23.08.26" — the compact date used in the rounds table. */
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  if (isCalendarDate(iso))
    return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
}

/** "Aug 23, 12:00" */
export function mediumDateTime(iso: string | null): string {
  if (!iso) return "";
  if (isCalendarDate(iso)) return longDate(iso);
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", ",");
}

/** "2 Sep 2026" */
export function longDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(isCalendarDate(iso) ? { timeZone: "UTC" } : {}),
  }).format(date);
}

/** "September 2026" */
export function monthYear(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    ...(isCalendarDate(iso) ? { timeZone: "UTC" } : {}),
  }).format(date);
}

/** Human label for a round format key ("stroke-play" → "Stroke Play"). */
export function formatLabel(format: string): string {
  const labels: Record<string, string> = {
    "stroke-play": "Stroke Play",
    stableford: "Stableford",
    "match-play": "Match Play",
    nassau: "Nassau",
    "total-only": "Total Only",
    "high-low": "High-Low",
    "declared-winner": "Declared Winner",
  };
  return (
    labels[format] ??
    format.replaceAll(/[._-]+/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

export function currentSeasonYear(now = new Date()): number {
  return now.getFullYear();
}

export function isInSeason(iso: string | null, year: number): boolean {
  if (!iso) return false;
  if (isCalendarDate(iso)) return Number(iso.slice(0, 4)) === year;
  const date = new Date(iso);
  return !Number.isNaN(date.getTime()) && date.getFullYear() === year;
}

/** The word for a scoring trend; null while there are too few rounds. */
export function formTrendLabel(
  direction: "improving" | "steady" | "declining" | "not-enough-data",
): string | null {
  if (direction === "improving") return "Improving";
  if (direction === "declining") return "Slipping";
  if (direction === "steady") return "Steady";
  return null;
}
